import { Renderer, CellModel, GridModel, renderers } from "@omegagrid/grid";
import { AxisItemProps } from "../tableAdapter";
import { dom } from "@omegagrid/core";
import type { CreateCellRendererOptions } from ".";
import { Extremes } from "./extremes";
import constants from "../../../constants";

const STYLE_BAR_COMMON: Partial<CSSStyleDeclaration> = {
	boxSizing: 'border-box',
	borderRadius: '2px'
};

const STYLE_POSITIVE: Partial<CSSStyleDeclaration> = Object.assign({
	background: '#34a06a',
	border: '1px solid #34a06a',
}, STYLE_BAR_COMMON);

const STYLE_NEGATIVE: Partial<CSSStyleDeclaration> = Object.assign({
	background: '#dd5546',
	border: '1px solid #dd5546',
}, STYLE_BAR_COMMON);

const STYLE_TOTAL_POSITIVE: Partial<CSSStyleDeclaration> = Object.assign({
	background: '#14633e',
	border: '1px solid #14633e',
}, STYLE_BAR_COMMON);

const STYLE_TOTAL_NEGATIVE: Partial<CSSStyleDeclaration> = Object.assign({
	background: '#9a2a21',
	border: '1px solid #9a2a21',
}, STYLE_BAR_COMMON);

const CONNECTOR_BORDER_STYLE = '1px dotted #aaa';

const WATERFALL_ROW_PADDING = 10;
const WATERFALL_COLUMN_PADDING = 5;

type WaterfallItem = [number, number]; // [open, close]

type WaterfallData = {
	min: number,
	max: number,
	items: Map<number, {min: number, max: number, data: WaterfallItem[]}>
}

const isTotalNode = (cell: CellModel, direction: 'column' | 'row') => {
	const opt = (direction == 'column' ? cell.row : cell.column).getProperty('viewOptions') as any;
	return opt?.waterfall?.total;
}

const calculateWaterfallData = (model: GridModel, direction: 'column' | 'row', firstIndex: number): WaterfallData => {
	const vect1 = direction == 'column' ? model.columns : model.rows;
	const vect2 = direction == 'column' ? model.rows : model.columns;

	const indices = vect1.filter(
		i => (i.properties as AxisItemProps)?.renderer === 'waterfall'
	).map(i => i.index);

	const data: WaterfallData = {
		min: Infinity,
		max: -Infinity,
		items: new Map<number, Extremes & {data: WaterfallItem[]}>(),
	};

	indices.forEach(i => {
		let level: number, prevLevel: number = null, open: number, close: number, prev: WaterfallItem;
		const stack: WaterfallItem[] = [];
		data.items.set(i, {min: Infinity, max: -Infinity, data: []});
		const serie = {
			min: Infinity,
			max: -Infinity,
			data: [] as WaterfallItem[]
		}

		for (let j = firstIndex; j < vect2.length; j++) {
			const item = vect2[j];
			level = item.level;
			const cell = item.cell(i);

			if (isTotalNode(cell, direction)) {
				// total node: anchored to the baseline, shows the whole
				// cumulative sum from zero
				open = 0;
				close = prev ? prev[1] : (cell.value as number || 0);
			} else {
				if (!prev) {
					open = 0;
				} else if (level > prevLevel) {
					open = prev[0];
					stack.push(prev);
				} else if (level < prevLevel) {
					let diff = prevLevel - level;
					while (diff > 0 && stack.length > 0) {
						prev = stack.pop();
						diff--;
					}
					open = prev[1];
				} else {
					open = prev[1];
				}

				close = open + (cell.value as number || 0);
			}

			prev = [open, close];
			serie.data.push([open, close]);
			prevLevel = level;

			data.min = Math.min(data.min, open, close);
			data.max = Math.max(data.max, open, close);
			serie.min = Math.min(serie.min, open, close);
			serie.max = Math.max(serie.max, open, close);
		}

		data.items.set(i, serie);
	});

	return data;
}

export const createColumnWaterfallRenderer = (opt: CreateCellRendererOptions): Renderer => {
	let data: WaterfallData = null;
	const globalExtremes = opt.scope == 'global';
	
	return (div: HTMLDivElement, cell: CellModel) => {
		// calculate all data during render of the first cell
		data ??= calculateWaterfallData(cell.model, opt.direction, opt.firstIndex);

		renderers.BaseRenderer(div, cell);
		
		const c = cell.c;
		const w = cell.column.width;
		const h = cell.row.height;
		const bar = dom.createElement('div');
		
		const totalNode = isTotalNode(cell, opt.direction);

		const item = data.items.get(c).data[cell.row.index];
		let [open, close] = item;
		let negative = open > close;
		if (negative) [open, close] = [close, open];

		const min = globalExtremes ? data.min : data.items.get(c).min;
		const max = globalExtremes ? data.max : data.items.get(c).max;

		if (min < 0) {
			open -= min;
			close -= min;
		}

		let left = w * open / (max - min);
		let width = Math.abs(left - (w * close / (max - min)));
		// zero (and very small) values take up at least 1px in width
		width = Math.max(width, 1);

		dom.setPosition(bar, {t: WATERFALL_COLUMN_PADDING, b: WATERFALL_COLUMN_PADDING, l: left});
		dom.setSize(bar, {w: width});
		Object.assign(bar.style, totalNode
			? (negative ? STYLE_TOTAL_NEGATIVE : STYLE_TOTAL_POSITIVE)
			: (negative ? STYLE_NEGATIVE : STYLE_POSITIVE));
		div.appendChild(bar);

		// if (!cell.row.hasChildren) {
			const line1 = cell.row.visibleIndex > 0 ? dom.createElement('div') : null;
			const line2 = cell.row.visibleIndex < cell.model.visibleRows.length - 1 ? dom.createElement('div') : null;

			// anchor the lines exactly onto the bar's vertical edge (right edge = left + width - 1),
			// so they overlap the border and don't create an optical double border
			if (line1) {
				dom.setPosition(line1, {
					t: -5,
					b: totalNode ? (h / 2) : WATERFALL_COLUMN_PADDING,
					l: left + (negative || totalNode ? width - 1 : 0)
				});
				dom.setSize(line1, {w: 1});
				line1.style.borderLeft = CONNECTOR_BORDER_STYLE;
				div.appendChild(line1);
			}

			if (line2) {
				dom.setPosition(line2, {
					t: totalNode ? (h / 2 - 1) : constants.CELL_CHART_PADDING,
					b: 0,
					l: left + (negative ? 0 : width - 1)
				});
				dom.setSize(line2, {w: 1});
				line2.style.borderLeft = CONNECTOR_BORDER_STYLE;
				div.appendChild(line2);
			}
		// }
		
		return div;
	};
}

export const createRowWaterfallRenderer = (opt: CreateCellRendererOptions): Renderer => {
	let data: WaterfallData = null;
	const globalExtremes = opt.scope == 'global';

	return (div: HTMLDivElement, cell: CellModel) => {
		// calculate all data during render of the first cell
		data ??= calculateWaterfallData(cell.model, opt.direction, opt.firstIndex);

		renderers.BaseRenderer(div, cell);
		div.style.overflow = 'visible';

		const r = cell.r;
		const h = cell.row.height;
		const w = cell.column.width;
		const bar = dom.createElement('div');

		const totalNode = isTotalNode(cell, opt.direction);
		
		const item = data.items.get(r).data[cell.column.index - opt.firstIndex];
		let [open, close] = item;
		let negative = open > close;
		if (negative) [open, close] = [close, open];

		const min = globalExtremes ? data.min : data.items.get(r).min;
		const max = globalExtremes ? data.max : data.items.get(r).max;

		if (min < 0) {
			open -= min;
			close -= min;
		}

		// vertical bars grow from the bottom of the cell upwards
		let bottom = h * open / (max - min);
		let height = Math.abs(bottom - (h * close / (max - min)));
		// zero (and very small) values take up at least 1px in height
		height = Math.max(height, 1);

		if (totalNode) bottom = 0;

		dom.setPosition(bar, {l: WATERFALL_ROW_PADDING, r: WATERFALL_ROW_PADDING, b: bottom});
		dom.setSize(bar, {h: height});
		Object.assign(bar.style, totalNode
			? (negative ? STYLE_TOTAL_NEGATIVE : STYLE_TOTAL_POSITIVE)
			: (negative ? STYLE_NEGATIVE : STYLE_POSITIVE));
		div.appendChild(bar);

		const line1 = cell.column.visibleIndex > 0 ? dom.createElement('div') : null;
		const line2 = cell.column.visibleIndex < cell.model.visibleColumns.length - 1 ? dom.createElement('div') : null;

		// anchor the lines exactly onto the bar's horizontal edge (top edge = bottom + height - 1),
		// so they overlap the border and don't create an optical double border
		if (line1) {
			dom.setPosition(line1, {
				l: -5,
				r: totalNode ? (w / 2) : (WATERFALL_ROW_PADDING + 1),
				b: bottom + (negative || totalNode ? height - 1 : -1)
			});
			dom.setSize(line1, {h: 1});
			line1.style.borderTop = CONNECTOR_BORDER_STYLE;
			div.appendChild(line1);
		}

		if (line2) {
			dom.setPosition(line2, {
				l: totalNode ? (w / 2 - 1) : (WATERFALL_ROW_PADDING + 1),
				r: 0,
				b: bottom + (negative ? 0 : height - 1)
			});
			dom.setSize(line2, {h: 1});
			line2.style.borderTop = CONNECTOR_BORDER_STYLE;
			div.appendChild(line2);
		}

		return div;
	};
}

export const createWaterfallRenderer = (opt: CreateCellRendererOptions): Renderer => opt.direction == 'column'
	? createColumnWaterfallRenderer(opt)
	: createRowWaterfallRenderer(opt);

